import React from 'react';
import App from '../../App';
import { getPostLoginRedirectPath, STORAGE_KEYS } from '../../constants/redirectPaths';
import { useAuth } from '../../contexts/AuthContext';
import LoginFlow from '../pages/LoginFlow';

/**
 * Authentication routing component
 * Shows LoginFlow if not authenticated, App if authenticated
 * 
 * IMPORTANT: This component captures the post-login redirect path
 * BEFORE App renders, to prevent timing issues with sessionStorage
 */
const AuthenticatedApp: React.FC = () => {
	const { isAuthenticated, isAuthReady, customer } = useAuth();

	// Use ref to capture redirect path ONCE when transitioning from unauthenticated to authenticated
	// This must happen BEFORE App renders, otherwise the path might be cleared
	const redirectPathRef = React.useRef<string | null>(null);
	const wasAuthenticatedRef = React.useRef<boolean>(false);

	// When user becomes authenticated, capture the redirect path immediately
	if (isAuthenticated && customer && !wasAuthenticatedRef.current) {
		// First time authenticated - capture the path NOW before anything else renders
		const storedPath = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_PATH);

		if (storedPath) {
			redirectPathRef.current = getPostLoginRedirectPath();
		}
		wasAuthenticatedRef.current = true;
	}

	if (!isAuthReady) {
		return null;
	}

	if (isAuthenticated && customer) {
		return <App redirectPath={redirectPathRef.current} />;
	}

	return <LoginFlow />;
};

export default AuthenticatedApp;